import { type HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

const variants: Record<BadgeVariant, string> = {
  default: 'bg-surface-muted text-ink-secondary',
  success: 'bg-success-bg text-success-text ring-1 ring-success-ring',
  warning: 'bg-warning-bg text-warning-text ring-1 ring-warning-ring',
  danger: 'bg-danger-bg text-danger-text ring-1 ring-danger-ring',
  info: 'bg-info-bg text-info-text ring-1 ring-info-ring',
  muted: 'bg-surface-muted text-ink-faint',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

export function Badge({ variant = 'default', className, ...props }: BadgeProps) {
  return (
    <span
      className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium', variants[variant], className)}
      {...props}
    />
  );
}

export function conflictBadge(type: string | null) {
  const map: Record<string, BadgeVariant> = {
    NEW: 'success',
    UPDATE: 'info',
    DUPLICATE: 'muted',
    CONTRADICTION: 'danger',
  };
  return map[type ?? ''] ?? 'default';
}

export function statusBadge(status: string) {
  const map: Record<string, BadgeVariant> = {
    PENDING: 'warning',
    APPROVED: 'success',
    REJECTED: 'danger',
    AUTO_APPLIED: 'info',
    SUPERSEDED: 'muted',
    KEPT_BOTH: 'default',
  };
  return map[status] ?? 'default';
}

export function sourceBadge(sourceType: string) {
  const map: Record<string, BadgeVariant> = {
    SLACK: 'info',
    FILE_UPLOAD: 'success',
    MANUAL: 'default',
    CLI_IMPORT: 'muted',
    GRANOLA: 'warning',
    MCP: 'info',
    USER_EDIT: 'success',
    USER_FEEDBACK: 'info',
    LINKED_DOCUMENT: 'muted',
    EMAIL: 'info',
    DRIVE_FILE: 'success',
    AUTOMATION_EDIT: 'warning',
  };
  return map[sourceType] ?? 'default';
}
